import React from "react";
import { ArrowUpLeft, LayoutGrid, Search, Tag } from "lucide-react";
import SearchResultCard from "@/components/atoms/search/searchResultCard";
import { Locales } from "@/lib/i18n";
import { useHits } from "react-instantsearch";
import { ProductHit } from "@/components/atoms/search/searchbar";
import Link from "next/link";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";

interface CustomSuggestionsProps {
  query: string;
  setQuery: (value: string) => void;
  locale: Locales;
  isInNavbar?: boolean;
}

interface CategoryMatch {
  lvl0: string;
  lvl1?: string;
  lvl2?: string;
}

type HitType = "product" | "category" | "tag";

interface ClassifiedHit {
  type: HitType;
  hit: ProductHit;
}

const CustomSuggestions: React.FC<CustomSuggestionsProps> = ({ query, setQuery, locale, isInNavbar }) => {
  const { hits } = useHits<ProductHit>();

  if (hits.length === 0) return null;

  const highlightQuery = (text: string): React.ReactNode => {
    if (!query.trim()) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      part?.toLowerCase() === query?.toLowerCase() ? (
        <span key={index} className="font-bold">
          {part}
        </span>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  const classifyHit = (hit: ProductHit, searchQuery: string): HitType => {
    const q = searchQuery?.toLowerCase().trim();

    const inTitle = hit.title?.toLowerCase().includes(q);

    const lvl0 = Array.isArray(hit.categories?.lvl0)
      ? hit.categories.lvl0.join(" ").toLowerCase()
      : hit.categories?.lvl0?.toString().toLowerCase();

    const lvl1 = Array.isArray(hit.categories?.lvl1)
      ? hit.categories.lvl1.join(" ").toLowerCase()
      : hit.categories?.lvl1?.toString().toLowerCase();

    const inCategory = lvl0?.includes(q) || lvl1?.includes(q);

    const inTags = hit.tags?.some((tag) => tag.toLowerCase().includes(q));

    if (inTitle) return "product";
    if (inCategory) return "category";
    if (inTags) return "tag";

    return "product";
  };

  const getClassifiedHits = (): ClassifiedHit[] => {
    return hits.map((hit) => ({
      type: classifyHit(hit, query),
      hit,
    }));
  };

  const getProductMatches = (classifiedHits: ClassifiedHit[]): ProductHit[] => {
    return classifiedHits
      .filter((item) => item.type === "product")
      .map((item) => item.hit)
      .slice(0, 3);
  };

  const getCategoryMatches = (classifiedHits: ClassifiedHit[]): CategoryMatch[] => {
    const seen = new Set<string>();
    const matches: CategoryMatch[] = [];

    for (const item of classifiedHits) {
      if (item.type !== "category" || matches.length >= 3) break;

      const { lvl0 = "", lvl1 = "", lvl2 } = item.hit.categories || {};

      const keyLvl0 = Array.isArray(lvl0) ? lvl0[0] : lvl0;
      const keyLvl1 = Array.isArray(lvl1) ? lvl1[0] : lvl1;

      const key = `${keyLvl0?.toLowerCase()}>${keyLvl1?.toLowerCase()}`;

      if (!seen.has(key)) {
        seen.add(key);
        matches.push({ lvl0, lvl1, lvl2 });
      }
    }

    return matches;
  };

  const getTagMatches = (classifiedHits: ClassifiedHit[]): string[] => {
    const seen = new Set<string>();
    const matches: string[] = [];

    for (const item of classifiedHits) {
      if (item.type !== "tag" || !item.hit.tags) continue;

      for (const tag of item.hit.tags) {
        if (tag.toLowerCase().includes(query.toLowerCase()) && !seen.has(tag) && matches.length < 2) {
          seen.add(tag);
          matches.push(tag);
        }
      }
    }

    return matches;
  };

  const createCategorySlug = (category: CategoryMatch): string => {
    const slugify = (text: string): string => {
      return text
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^\w\-]+/g, "")
        .replace(/\-\-+/g, "-")
        .replace(/^-+/, "")
        .replace(/-+$/, "");
    };

    if (category.lvl1) {
      let catLvl1 = category.lvl1;

      if (Array.isArray(category.lvl1)) {
        catLvl1 = category.lvl1[0];
      }

      return catLvl1
        .split(">")
        .map((part) => slugify(part.trim()))
        .join("/");
    }

    return slugify(category.lvl0);
  };

  const classifiedHits = getClassifiedHits();
  const productMatches = getProductMatches(classifiedHits);
  const categoryMatches = getCategoryMatches(classifiedHits);
  const tagMatches = getTagMatches(classifiedHits);

  return (
    <div className="absolute top-full left-0 z-10 mt-2 w-full rounded-lg border border-gray-300 bg-white p-3.5 shadow-[0_1px_4px_0_#00000026]">
      <div className="flex flex-col gap-2.5 sm:flex-row">
        {/* Suggestions Section */}
        <div className={cn("sm:w-1/3", isInNavbar && "max-md:hidden")}>
          <p className="text-sm text-gray-400">{__("Suggestions", locale)}</p>
          <ul className="flex flex-col" role="listbox">
            {/* Product Title Matches */}
            {productMatches.slice(0, 4).map((product, index) => (
              <li
                key={`title-${index}`}
                onClick={() => setQuery(product.title)}
                className="flex cursor-pointer flex-col rounded px-1 py-2 hover:bg-gray-100"
              >
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Search size={16} />
                    <p>{highlightQuery(product.title)}</p>
                  </div>
                  <ArrowUpLeft size={20} />
                </div>
              </li>
            ))}

            {/* Category Matches */}
            {categoryMatches.slice(0, 4).map((category, index) => (
              <Link
                key={`category-${index}`}
                href={`${getPermalink(locale, "categories")}/${createCategorySlug(category)}`}
                className="flex cursor-pointer flex-col rounded px-1 py-2 hover:bg-gray-100"
              >
                <div className="flex items-center justify-between text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <LayoutGrid size={16} />
                    <div className="flex flex-col font-normal">
                      <p>{category.lvl0}</p>
                      {category.lvl1 ? <div className="text-xs text-neutral-400">{category.lvl1}</div> : null}
                    </div>
                  </div>
                </div>
              </Link>
            ))}

            {/* Tag Matches */}
            {tagMatches.map((tagName, index) => (
              <Link
                key={`tag-${index}`}
                href={`${getPermalink(locale, "tag")}/${tagName}`}
                className="flex cursor-pointer items-center justify-between rounded px-1 py-2 hover:bg-gray-100"
              >
                <Tag size={16} className="rotate-90" />
                <div className="text-sm">
                  <p>{highlightQuery(tagName)}</p>
                </div>
              </Link>
            ))}
          </ul>
        </div>

        {/* Products Preview Section */}
        <div className={cn("flex flex-col gap-2.5", isInNavbar ? "w-full md:w-2/3" : "sm:w-2/3")}>
          <p className={cn("text-sm text-gray-400", isInNavbar && "max-md:hidden")}>{__("Products", locale)}</p>
          <div
            className={cn(
              "grid w-full grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3",
              isInNavbar && "flex-col max-md:flex"
            )}
          >
            {hits.slice(0, 3).map((product, index) => (
              <div key={index} className={cn(index === 2 ? "hidden md:block" : "")}>
                <SearchResultCard key={index} isInNavbar={isInNavbar} product={product} locale={locale} />
              </div>
            ))}

            <div className="col-span-full sm:mt-9">
              <Link
                href={`${getPermalink(locale, "search")}/${query}`}
                className={cn(
                  "hover:bg-dark-primary flex w-full cursor-pointer items-center justify-center rounded-xs border bg-transparent py-2 text-sm font-semibold text-[#003349] transition-colors hover:text-white",
                  isInNavbar &&
                    "max-md:bg-pink-primary/10 max-md:hover:bg-pink-primary max-md:hover:text-white-primary max-md:text-pink-primary max-md:border-0"
                )}
              >
                {__("See all results", locale)}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSuggestions;
